'use client';

import { Checkbox } from '@kit/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';

import type { FormFieldDescriptor } from '../types/vertical';

interface DynamicFormStepProps {
  fields: FormFieldDescriptor[];
  fieldPrefix?: string;
}

/**
 * Format a camelCase or snake_case field name into a human-readable label.
 */
function formatLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\s/, '')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format an enum value into a human-readable option label.
 */
function formatEnumLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Renders a single dynamic form field based on its FormFieldDescriptor.
 */
function DynamicField({
  field,
  fieldPrefix,
}: {
  field: FormFieldDescriptor;
  fieldPrefix: string;
}) {
  const fieldName = `${fieldPrefix}${field.name}`;
  const label = field.description || formatLabel(field.name);

  // For array-type fields with enum values, render multi-select checkboxes
  if (field.type === 'array' && field.enumValues) {
    return (
      <MultiCheckboxField
        fieldName={fieldName}
        label={label}
        required={field.required}
        options={field.enumValues}
      />
    );
  }

  // For enum fields, render a select dropdown
  if (field.type === 'enum' && field.enumValues) {
    return (
      <FormField
        name={fieldName}
        render={({ field: formField }) => (
          <FormItem>
            <FormLabel>
              {label}
              {!field.required ? (
                <span className="text-muted-foreground ml-1 text-xs font-normal">
                  (optional)
                </span>
              ) : null}
            </FormLabel>
            <FormControl>
              <Select
                value={formField.value ?? ''}
                onValueChange={formField.onChange}
              >
                <SelectTrigger
                  className="w-full"
                  data-test={`field-${field.name}`}
                >
                  <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
                </SelectTrigger>

                <SelectContent>
                  {field.enumValues!.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {formatEnumLabel(opt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // For boolean fields, render a checkbox
  if (field.type === 'boolean') {
    return (
      <FormField
        name={fieldName}
        render={({ field: formField }) => (
          <FormItem>
            <div
              className="flex cursor-pointer items-center gap-3"
              onClick={() => formField.onChange(!(formField.value ?? false))}
              role="none"
            >
              <FormControl>
                <Checkbox
                  checked={formField.value ?? false}
                  onCheckedChange={formField.onChange}
                  data-test={`field-${field.name}`}
                />
              </FormControl>
              <FormLabel className="cursor-pointer font-normal">
                {label}
                {!field.required ? (
                  <span className="text-muted-foreground ml-1 text-xs">
                    (optional)
                  </span>
                ) : null}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // For number fields
  if (field.type === 'number') {
    return (
      <FormField
        name={fieldName}
        render={({ field: formField }) => (
          <FormItem>
            <FormLabel>
              {label}
              {!field.required ? (
                <span className="text-muted-foreground ml-1 text-xs font-normal">
                  (optional)
                </span>
              ) : null}
            </FormLabel>
            <FormControl>
              <Input
                type="number"
                placeholder={label}
                data-test={`field-${field.name}`}
                value={formField.value ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  formField.onChange(val === '' ? undefined : Number(val));
                }}
                onBlur={formField.onBlur}
                name={formField.name}
                ref={formField.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Default: text input
  return (
    <FormField
      name={fieldName}
      render={({ field: formField }) => (
        <FormItem>
          <FormLabel>
            {label}
            {!field.required ? (
              <span className="text-muted-foreground ml-1 text-xs font-normal">
                (optional)
              </span>
            ) : null}
          </FormLabel>
          <FormControl>
            <Input
              type="text"
              placeholder={label}
              data-test={`field-${field.name}`}
              {...formField}
              value={formField.value ?? ''}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Multi-checkbox group for array+enum fields (e.g. qualifications, eventTypes).
 */
function MultiCheckboxField({
  fieldName,
  label,
  required,
  options,
}: {
  fieldName: string;
  label: string;
  required: boolean;
  options: string[];
}) {
  return (
    <FormField
      name={fieldName}
      render={({ field: formField }) => {
        const currentValues: string[] = Array.isArray(formField.value)
          ? formField.value
          : [];

        return (
          <FormItem>
            <FormLabel>
              {label}
              {!required ? (
                <span className="text-muted-foreground ml-1 text-xs font-normal">
                  (optional)
                </span>
              ) : null}
            </FormLabel>
            <div className="grid grid-cols-2 gap-2">
              {options.map((opt) => {
                const isChecked = currentValues.includes(opt);
                const toggle = () => {
                  if (isChecked) {
                    formField.onChange(
                      currentValues.filter((v: string) => v !== opt),
                    );
                  } else {
                    formField.onChange([...currentValues, opt]);
                  }
                };

                return (
                  <div
                    key={opt}
                    className="flex cursor-pointer items-center gap-2"
                    onClick={toggle}
                    role="none"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          formField.onChange([...currentValues, opt]);
                        } else {
                          formField.onChange(
                            currentValues.filter((v: string) => v !== opt),
                          );
                        }
                      }}
                      data-test={`field-${fieldName}-${opt}`}
                    />
                    <Label className="cursor-pointer text-sm font-normal">
                      {formatEnumLabel(opt)}
                    </Label>
                  </div>
                );
              })}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/**
 * Renders a dynamic form step from an array of FormFieldDescriptors.
 * Maps field types to the appropriate @kit/ui components.
 */
export function DynamicFormStep({
  fields,
  fieldPrefix = 'customFields.',
}: DynamicFormStepProps) {
  return (
    <div className="flex flex-col gap-4">
      {fields.map((field) => (
        <DynamicField
          key={field.name}
          field={field}
          fieldPrefix={fieldPrefix}
        />
      ))}
    </div>
  );
}
